import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'antd';
import {
    ProductionsCard,
    UtilityDetailsPopupModal,
    ProposalSummaryCard,
    FinancingGridCard,
    CommerceHubPanel,
    FinancePartnerChecklistSection,
    ProductsFinanceHubProvider,
    QuotesContractsCard,
    AgreementsModal,
} from '..';
import { DASHBOARD_SECTION_NAV_ITEMS, type DashboardSectionPanelId } from '../dashboardSectionNav';
import { useDashboardStoreBindings } from '../hooks/useDashboardStoreBindings';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../../store/store';
import { useSystemDesignMissingItems } from '../useSystemDesignMissingItems';
import { useSystemDesignMissingFixOrchestrator } from '../useSystemDesignMissingFixOrchestrator';
import { sectionPanelForMissingItem, isDashboardCommerceReady, type SystemDesignMissingItemId } from '../systemDesignMissingItems';
import { hasAnyPricingProductEnabled } from '../../../../../helpers/financeSelectors';
import { SystemDesignFixFab } from '../SystemDesignFixFab';

function useProposalSummaryVisible(): boolean {
    const ppwValue = useViewerStore((s) => s.ppwValue);
    const totalSystemSize = useViewerStore((s) => s.totalSystemSize);
    return totalSystemSize > 0 && ppwValue > 0;
}
import { DashboardOpportunityHeaderBlock } from './DashboardOpportunityHeaderBlock';
import { DashboardV3SectionModals } from './dashboardV3/DashboardV3SectionModals';
export interface DashboardSplitMainPanelsProps {
    theme: 'dark' | 'light';
    siteAddress: any;
    titleSearchData: any;
    oppRefreshKey: number;
    setAddTitleContactsModalOpen: (next: boolean) => void;
    handleOpenBlackKnightLeadModal: () => void;
    dashboardAnnualUsage: number | null;
    dashboardSystemSizeKw: number | null;
    dashboardAvgDailyUsage: number | null;
    dashboardMultiplier: number | null;
    triggerPricingGridForDesign: (design: any) => void;
    priceQuote: any;
    isDashboardOpen: boolean;
    pricingFetchTrigger: number | null;
    pricingDesignForTrigger: any;
    setPricingFetchTrigger: React.Dispatch<React.SetStateAction<number | null>>;
    opportunityDetails: any;
    selectedDesignForPricing: any;
    setPricingGridModalOpen: (next: boolean) => void;
    opportunityID: any;
    dashboardSectionPanel: DashboardSectionPanelId;
    setDashboardSectionPanel: React.Dispatch<React.SetStateAction<DashboardSectionPanelId>>;
    designsDashboardSlot: React.ReactNode;
    /** V3: close dashboard overlay to return to 3D viewer */
    setIsDashboardOpen?: (next: boolean | ((prev: boolean) => boolean)) => void;
    /** Left rail AI Designs: inline section only — close the modal popup if it was open. */
    registerCloseAiDesignsModal?: (close: () => void) => void;
    /** Angular side-menu: Utility Information visibility. */
    showUtilityDetails?: boolean;
    /** Angular side-menu: System Design visibility. */
    showSystemDesign?: boolean;
}



/**
 * Main dashboard content to the right of {@link DashboardSectionSlideRail}: full grid (“all”) or single-section panels (Designs, Customer, …).
 */
export const DashboardSplitMainPanels: React.FC<DashboardSplitMainPanelsProps> = ({
    theme,
    siteAddress,
    titleSearchData,
    oppRefreshKey,
    setAddTitleContactsModalOpen,
    handleOpenBlackKnightLeadModal,
    dashboardAnnualUsage,
    dashboardSystemSizeKw,
    dashboardAvgDailyUsage,
    dashboardMultiplier,
    triggerPricingGridForDesign,
    priceQuote,
    isDashboardOpen,
    pricingFetchTrigger,
    pricingDesignForTrigger,
    setPricingFetchTrigger,
    opportunityDetails,
    selectedDesignForPricing,
    setPricingGridModalOpen,
    opportunityID,
    dashboardSectionPanel,
    setDashboardSectionPanel,
    designsDashboardSlot,
    registerCloseAiDesignsModal,
    showUtilityDetails = true,
    showSystemDesign = true,
}) => {
    const { loadingCompleted, refreshingShadeFromStore, designListFromStore, siteDesignDetailsFromStore,
    } = useDashboardStoreBindings({});
    const contactsOpen = useOneButtonStore((s) => s.dashboardContactsModalOpen);
    const setDashboardContactsModalOpen = useOneButtonStore((s) => s.setDashboardContactsModalOpen);
    const setDashboardSalesPersonModalOpen = useOneButtonStore((s) => s.setDashboardSalesPersonModalOpen);

    const [aiDesignsOpen, setAiDesignsOpen] = useState(false);

    useEffect(() => {
        registerCloseAiDesignsModal?.(() => setAiDesignsOpen(false));
    }, [registerCloseAiDesignsModal]);

    const designsMenubarSignal = useViewerStore((s) => s.designsMenubarSignal);
    const prevMenubarSignalRef = useRef(designsMenubarSignal);
    useEffect(() => {
        if (designsMenubarSignal > prevMenubarSignalRef.current) {
            setAiDesignsOpen(false);
        }
        prevMenubarSignalRef.current = designsMenubarSignal;
    }, [designsMenubarSignal]);

    const opportunityNumericId =
        opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? null;
    const dashboardOpportunityId = sessionStorage.getItem('SalesForceId') || '';
    const dashboardOpportunityName = sessionStorage.getItem('opportunityName') || '';
    const dashboardStageNumber = sessionStorage.getItem('currentStage') || '';
    const dashboardStageStatus = sessionStorage.getItem('stageStatus') || '';
    const dashboardUtilityCompany = sessionStorage.getItem('Utility_Company') || '';
    const productsHubSalesForceId =
        opportunityDetails?.SalesForce_Id ??
        (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) ??
        undefined;
    const designAndShadeReady = Boolean(loadingCompleted && !refreshingShadeFromStore);
    const hasPrimaryDesign = Array.isArray(designListFromStore) && designListFromStore.length > 0;
    const hasSiteDesignDetails = Array.isArray(siteDesignDetailsFromStore) && siteDesignDetailsFromStore.length > 0;
    const isDashboardDataLoading = !designAndShadeReady && !hasPrimaryDesign && !hasSiteDesignDetails;
    /** Contacts come from the opportunity, so background shade passes must not blank the editor. */
    const isContactsDataLoading = !opportunityDetails;

    const opportunityHeaderProps = useMemo(
        () => ({
            theme,
            loading: isDashboardDataLoading,
            opportunityId: dashboardOpportunityId,
            opportunityName: dashboardOpportunityName,
            stageStatus: dashboardStageStatus,
            stageNumber: dashboardStageNumber,
            utilityCompany: dashboardUtilityCompany,
            siteAddress,
            refreshKey: oppRefreshKey,
            isTitleContactExist: titleSearchData === null ? null : titleSearchData.length > 0,
            onAddTitleContactsClick: () => setAddTitleContactsModalOpen(true),
            onBlackKnightTitleCheckClick: handleOpenBlackKnightLeadModal,
        }),
        [
            theme,
            isDashboardDataLoading,
            dashboardOpportunityId,
            dashboardOpportunityName,
            dashboardStageStatus,
            dashboardStageNumber,
            dashboardUtilityCompany,
            siteAddress,
            oppRefreshKey,
            titleSearchData,
            setAddTitleContactsModalOpen,
            handleOpenBlackKnightLeadModal,
        ]
    );

    const showProposalSummary = useProposalSummaryVisible();

    const contactsModalProps = useMemo(
        () => ({
            loading: isContactsDataLoading,
            opportunityId: dashboardOpportunityId,
            isTitleContactExist: titleSearchData === null ? null : titleSearchData.length > 0,
            onAddTitleContactsClick: () => setAddTitleContactsModalOpen(true),
            onBlackKnightTitleCheckClick: handleOpenBlackKnightLeadModal,
        }),
        [
            isContactsDataLoading,
            dashboardOpportunityId,
            titleSearchData,
            setAddTitleContactsModalOpen,
            handleOpenBlackKnightLeadModal,
        ]
    );

    const systemDesignMissingItems = useSystemDesignMissingItems();
    const commerceReady = isDashboardCommerceReady(systemDesignMissingItems);
    const pricingProductsEnabled = useOneButtonStore((s) => s.pricingProductsEnabled);
    const anyPricingProductEnabled = hasAnyPricingProductEnabled(pricingProductsEnabled);
    const showFinancingGrid = commerceReady || anyPricingProductEnabled;
    const { fixMissingItem, bindExecutor, bindCustomerExecutor } = useSystemDesignMissingFixOrchestrator({
        dashboardSectionPanel,
        setDashboardSectionPanel,
    });

    const handleCustomerMissingItem = useCallback(
        (id: SystemDesignMissingItemId) => {
            if (id === 'title-contacts') {
                setAddTitleContactsModalOpen(true);
                return;
            }
            if (id === 'primary-contact') {
                setDashboardContactsModalOpen(true);
                return;
            }
            if (id === 'sales-person') {
                setDashboardSalesPersonModalOpen(true);
            }
        },
        [
            setAddTitleContactsModalOpen,
            setDashboardContactsModalOpen,
            setDashboardSalesPersonModalOpen,
        ],
    );

    useEffect(() => {
        bindCustomerExecutor(handleCustomerMissingItem);
        return () => bindCustomerExecutor(null);
    }, [bindCustomerExecutor, handleCustomerMissingItem]);

    const scrollToSystemDesign = useCallback(() => {
        requestAnimationFrame(() => {
            document
                .querySelector('.obd-system-design-row, .obd-card--system-design')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, []);

    const productionsCardFixProps = useMemo(
        () => ({
            onBindSystemDesignFixExecutor: bindExecutor,
            onFixMissingItem: fixMissingItem,
        }),
        [bindExecutor, fixMissingItem],
    );

    return (
        <ProductsFinanceHubProvider salesForceId={productsHubSalesForceId}>
        <div className="obd-masonry-wrap obd-masonry-wrap--grid obd-masonry-wrap--section-panels">
            {dashboardSectionPanel === 'all' ? (
                <>
                    <div className="obd-masonry obd-masonry--dashboard">
                        <DashboardOpportunityHeaderBlock {...opportunityHeaderProps} />
                        {showSystemDesign ? (
                        <div className="obd-masonry__item obd-masonry__item--wide obd-system-design-row">
                            <ProductionsCard
                                mergeWithSystem
                                {...productionsCardFixProps}
                                loading={isDashboardDataLoading}
                                systemMetricsProps={{
                                    annualUsageKwh: dashboardAnnualUsage,
                                    systemSizeKw: dashboardSystemSizeKw,
                                    avgDailyUsageKwh: dashboardAvgDailyUsage,
                                    multiplier: dashboardMultiplier,
                                }}
                                onSolarPricingClick={(design) => {
                                    triggerPricingGridForDesign(design);
                                }}
                            />
                        </div>
                        ) : null}
                        {showFinancingGrid ? (
                        <div
                            className="obd-masonry__item obd-masonry__item--wide obd-dashboard-financing-row"
                            data-obd-guide="financing-grid"
                        >
                            <FinancingGridCard
                                priceQuote={priceQuote}
                                dashboardOpen={isDashboardOpen}
                                fetchTrigger={pricingFetchTrigger}
                                designForTrigger={pricingDesignForTrigger}
                                onRefreshRequested={() => useOneButtonStore.setState({ pricingNetworkRefreshTrigger: Date.now() })}
                                systemDesignMissingItems={systemDesignMissingItems}
                            />
                        </div>
                        ) : null}
                        {showProposalSummary ? (
                            <div className="obd-masonry__item obd-masonry__item--wide">
                                <ProposalSummaryCard />
                            </div>
                        ) : null}
                    </div>
                    {commerceReady ? (
                    <div className="obd-dashboard-fullwidth-pricing-commerce obd-dashboard-fullwidth-pricing-commerce--commerce-only">
                        <div className="obd-dashboard-fullwidth-pricing-commerce__commerce">
                            <CommerceHubPanel
                                salesForceId={productsHubSalesForceId}
                                loading={isDashboardDataLoading}
                                onSolarPricingClick={() => {
                                    if (selectedDesignForPricing) {
                                        triggerPricingGridForDesign(selectedDesignForPricing);
                                        return;
                                    }
                                    setPricingGridModalOpen(true);
                                    setPricingFetchTrigger(Date.now());
                                }}
                                opportunityNumericId={
                                    opportunityID ??
                                    opportunityDetails?.Opportunity_Id ??
                                    opportunityDetails?.OpportunityId ??
                                    null
                                }
                                priceQuote={priceQuote}
                            >
                                <FinancePartnerChecklistSection />
                            </CommerceHubPanel>
                        </div>
                    </div>
                    ) : null}
                </>
            ) : (
                <div
                    className={`obd-dashboard-section-single obd-theme-${theme}`}
                    role="region"
                    aria-label={
                        DASHBOARD_SECTION_NAV_ITEMS.find((s) => s.id === dashboardSectionPanel)?.label ??
                        'Dashboard section'
                    }
                >
                    {dashboardSectionPanel === 'designs' && designsDashboardSlot ? (
                        <div className="obd-dashboard-section-single__designs obd-dashboard-section-single__designs--inline-ai">
                            {designsDashboardSlot}
                        </div>
                    ) : null}
                    {dashboardSectionPanel === 'customer' ? (
                        <DashboardOpportunityHeaderBlock {...opportunityHeaderProps} />
                    ) : null}
                    {dashboardSectionPanel === 'system' && showSystemDesign ? (
                        <div className="obd-middle-row__col obd-middle-row__col--system-stack obd-dashboard-section-single__system">
                            <ProductionsCard
                                mergeWithSystem
                                alwaysExpanded
                                {...productionsCardFixProps}
                                loading={isDashboardDataLoading}
                                systemMetricsProps={{
                                    annualUsageKwh: dashboardAnnualUsage,
                                    systemSizeKw: dashboardSystemSizeKw,
                                    avgDailyUsageKwh: dashboardAvgDailyUsage,
                                    multiplier: dashboardMultiplier,
                                }}
                                onSolarPricingClick={(design) => {
                                    triggerPricingGridForDesign(design);
                                }}
                            />
                        </div>
                    ) : null}
                    {dashboardSectionPanel === 'utility' && showUtilityDetails ? (
                        <UtilityDetailsPopupModal
                            open
                            onClose={() => setDashboardSectionPanel('all')}
                        />
                    ) : null}
                    {dashboardSectionPanel === 'financing' ? (
                        showFinancingGrid ? (
                        <div className="obd-dashboard-section-single__financing" data-obd-guide="financing-grid">
                            <FinancingGridCard
                                priceQuote={priceQuote}
                                dashboardOpen={isDashboardOpen}
                                fetchTrigger={pricingFetchTrigger}
                                designForTrigger={pricingDesignForTrigger}
                                onRefreshRequested={() => useOneButtonStore.setState({ pricingNetworkRefreshTrigger: Date.now() })}
                                systemDesignMissingItems={systemDesignMissingItems}
                            />
                        </div>
                        ) : (
                            <Typography.Paragraph
                                type="secondary"
                                className="obd-dashboard-section-empty"
                            >
                                Enable Price on a system design card (Solar, Roof, or Battery) to load financing options.
                            </Typography.Paragraph>
                        )
                    ) : null}
                    {dashboardSectionPanel === 'refer' ? (
                        <div className="obd-dashboard-section-single__refer">
                            <Typography.Title level={5} style={{ marginTop: 0 }}>
                                Refer a friend
                            </Typography.Title>
                            <Typography.Paragraph type="secondary">
                                Referral tools are not wired in this view yet. Use your team&apos;s referral program in Salesforce
                                or your portal when available.
                            </Typography.Paragraph>
                        </div>
                    ) : null}
                    {dashboardSectionPanel === 'quotes' ? (
                        commerceReady ? (
                        <div className="obd-dashboard-section-single__quotes obd-dashboard-commerce-stack">
                            <FinancePartnerChecklistSection />
                            <QuotesContractsCard
                                layout="modal"
                                embeddedInDashboardSection
                                priceQuote={priceQuote}
                                quotesModalVisible={dashboardSectionPanel === 'quotes'}
                                onRequestOpenAgreements={() => setDashboardSectionPanel('agreements')}
                            />
                        </div>
                        ) : (
                            <Typography.Paragraph
                                type="secondary"
                                className="obd-dashboard-section-empty"
                            >
                                Complete required customer, utility, and design steps before quotes unlock.
                            </Typography.Paragraph>
                        )
                    ) : null}
                    {dashboardSectionPanel === 'agreements' ? (
                        commerceReady ? (
                        <div className="obd-dashboard-section-single__agreements obd-dashboard-commerce-stack">
                            <FinancePartnerChecklistSection />
                            <AgreementsModal
                                variant="embedded"
                                open
                                onClose={() => setDashboardSectionPanel('all')}
                                opportunityId={opportunityNumericId}
                            />
                        </div>
                        ) : (
                            <Typography.Paragraph type="secondary">
                                Complete required customer, utility, and design steps before agreements unlock.
                            </Typography.Paragraph>
                        )
                    ) : null}
                </div>
            )}
            <DashboardV3SectionModals
                theme={theme}
                dashboardUiVariant="v2"
                contactsOpen={contactsOpen}
                onCloseContacts={() => setDashboardContactsModalOpen(false)}
                aiDesignsOpen={aiDesignsOpen}
                onCloseAiDesigns={() => setAiDesignsOpen(false)}
                designsDashboardSlot={designsDashboardSlot}
                contactsProps={contactsModalProps}
            />
            <SystemDesignFixFab
                items={systemDesignMissingItems}
                onFixItem={fixMissingItem}
                onScrollToSection={() => {
                    const first = systemDesignMissingItems[0];
                    if (first && sectionPanelForMissingItem(first.id) === 'system') {
                        scrollToSystemDesign();
                    }
                }}
            />
        </div>
        </ProductsFinanceHubProvider>
    );
};
